import { getActiveEpicState, type LaunchMode } from '@/features/auth/launchContext'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'

function normalizeFhirLogicalId(id: string): string {
  const t = id.trim()
  return t.startsWith('Patient/') ? t.slice('Patient/'.length) : t
}

/**
 * Patient id string used for GET /ordering/sessions?patientId=… — must match
 * `canonicalOrder.patient.externalPatientId` saved on the draft (from `formInfo.patient.id`).
 */
export function resolvePatientIdForDraftLookup(params: {
  launchMode: LaunchMode
  orderFormInfoData: OrderFormInfoData
  epicOAuthState: string
}): string | null {
  const fromForm = (params.orderFormInfoData.patient?.id ?? '').trim()

  if (params.launchMode === 'standalone') {
    return fromForm || null
  }

  let candidate = fromForm
  if (params.epicOAuthState) {
    try {
      const ctx = EpicAuthClient.getInstance().getContext(params.epicOAuthState)
      const ctxPid = ctx.fhirPatientId?.trim()
      if (ctxPid && fromForm) {
        const nCtx = normalizeFhirLogicalId(ctxPid)
        const nForm = normalizeFhirLogicalId(fromForm)
        if (nCtx !== nForm) {
          console.warn('[orderDraftPatientId] Epic form patient id vs launch context mismatch', {
            fromForm,
            fhirPatientId: ctxPid,
          })
          candidate = ctxPid
        }
      } else if (ctxPid && !fromForm) {
        candidate = ctxPid
      }
    } catch {
      // EpicAuthClient missing env, etc.
    }
  }

  const out = candidate.trim()
  return out || null
}

/**
 * Launch-context patient id for the active Epic session. A reload after an Epic
 * discard has neither a session nor order form data, so this is the only way left
 * to find the patient whose discard marker decides what the page shows.
 */
export function readEpicLaunchPatientId(epicOAuthState?: string | null): string | null {
  try {
    const state = epicOAuthState?.trim() || getActiveEpicState()
    if (!state) return null
    return EpicAuthClient.getInstance().getContext(state).fhirPatientId?.trim() || null
  } catch {
    return null
  }
}

/**
 * Patient id read straight off a persisted ordering session, for the window where
 * the page still has a session id from the URL but no hydrated order form data.
 */
export function readSessionPatientLookupId(session: Record<string, unknown>): string | null {
  const state = session['state'] as Record<string, unknown> | undefined
  const canonical = state?.['canonicalOrder'] as { patient?: { externalPatientId?: string | null } } | undefined
  const id = canonical?.patient?.externalPatientId?.trim()
  return id || null
}
